/**
 * Client-side image compression + resize.
 * Phone photos are often 5–12MB (and HEIC on iPhone). We downscale to a sane max
 * dimension and re-encode as JPEG before upload — smaller, faster, and it converts
 * HEIC/HEIF to a web-friendly format on devices that can decode it.
 */
function loadViaImage(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export async function compressImage(file, { maxDim = 1920, quality = 0.82 } = {}) {
  if (!file) return file;
  // Keep animated GIFs untouched (canvas would flatten them).
  if (file.type === 'image/gif') return file;

  let source = null;
  try {
    source = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (_) {
    source = await loadViaImage(file);
  }
  if (!source) return file; // couldn't decode (e.g. unsupported HEIC) — upload as-is

  const w0 = source.width;
  const h0 = source.height;
  if (!w0 || !h0) return file;

  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0, w, h);
  if (source.close) source.close();

  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  if (!blob) return file;

  const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
}
